import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Properties", path: "/properties" },
  { label: "Products", path: "/social-housing" },
  { label: "Brochure", path: "/brochure" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const isTransparent = location.pathname === "/" && !scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent ? "bg-transparent" : "bg-white border-b border-slate-100"
        }`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          boxShadow: !isTransparent ? "0 1px 20px rgba(7,26,46,0.07)" : "none",
        }}
      >
        {/* Gold shimmer top line */}
        {!isTransparent && (
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: "linear-gradient(90deg, transparent 0%, #C9A84C 30%, #E2C27A 50%, #C9A84C 70%, transparent 100%)" }}
          />
        )}

        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                isTransparent
                  ? "border-[#C9A84C]/50 bg-[#C9A84C]/15 text-[#C9A84C]"
                  : "border-[#C9A84C]/40 bg-[#071A2E] text-[#C9A84C]"
              }`}
              style={!isTransparent ? { boxShadow: "0 0 14px rgba(201,168,76,0.22)" } : {}}
            >
              <i className="ri-home-4-fill text-base leading-none" />
            </div>
            <div className="hidden sm:block">
              <span
                className={`font-bold text-[15px] tracking-[0.08em] uppercase leading-none block ${
                  isTransparent ? "text-white" : "text-[#071A2E]"
                }`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Thorpe Dellow
              </span>
              <span
                className={`text-[9px] tracking-[0.2em] uppercase leading-none block mt-0.5 ${
                  isTransparent ? "text-[#C9A84C]/80" : "text-[#C9A84C]"
                }`}
              >
                Structured Investments
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center">
            <div className={`w-px h-5 mr-7 ${isTransparent ? "bg-white/20" : "bg-slate-200"}`} />
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative group flex items-center px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-[0.1em] uppercase transition-all cursor-pointer whitespace-nowrap mr-0.5 ${
                    active
                      ? isTransparent ? "text-[#C9A84C]" : "text-[#071A2E]"
                      : isTransparent ? "text-white/75 hover:text-white" : "text-slate-500 hover:text-[#071A2E]"
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute bottom-0 left-3 right-3 h-[1.5px] rounded-full transition-transform duration-200 ${
                      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100 origin-left"
                    }`}
                    style={{ background: "linear-gradient(90deg, #C9A84C, #E2C27A, #C9A84C)" }}
                  />
                </Link>
              );
            })}
            <div className={`w-px h-5 ml-7 ${isTransparent ? "bg-white/20" : "bg-slate-200"}`} />
          </div>

          {/* Desktop CTA Group */}
          <div className="hidden lg:flex items-center gap-2">
            <div className="flex flex-col items-end gap-0.5">
              <a
                href="tel:+442030976185"
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isTransparent ? "text-white/60 hover:text-white hover:bg-white/8" : "text-slate-400 hover:text-[#071A2E] hover:bg-slate-50"
                }`}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-phone-line text-xs leading-none" />
                </div>
                +44 20 3097 6185
              </a>
              <a
                href="tel:+14372922296"
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isTransparent ? "text-white/60 hover:text-white hover:bg-white/8" : "text-slate-400 hover:text-[#071A2E] hover:bg-slate-50"
                }`}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-phone-line text-xs leading-none" />
                </div>
                +1 437 292 2296
              </a>
            </div>

            <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isTransparent ? "bg-white/25" : "bg-slate-200"}`} />

            <a
              href="https://portal.thorpedellow.com"
              target="_blank"
              rel="nofollow noreferrer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-semibold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap border ${
                isTransparent
                  ? "border-[#C9A84C]/50 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]"
                  : "border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/8 hover:border-[#C9A84C]/70"
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-user-line text-xs leading-none" />
              </div>
              Portal
            </a>

            <Link
              to="/contact"
              className="relative flex items-center gap-1.5 px-5 py-2 rounded-lg text-[11px] font-bold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap overflow-hidden group"
              style={
                isTransparent
                  ? { background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", color: "#071A2E" }
                  : { background: "#071A2E", color: "#FFFFFF", boxShadow: "0 2px 14px rgba(7,26,46,0.28)" }
              }
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={
                  isTransparent
                    ? { background: "linear-gradient(135deg, #B8953B 0%, #C9A84C 100%)" }
                    : { background: "linear-gradient(135deg, #0D2B4A 0%, #071A2E 100%)" }
                }
              />
              <span className="relative flex items-center gap-1.5">
                Get in Touch
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs leading-none" />
                </div>
              </span>
            </Link>
          </div>

          {/* Tablet mid CTA */}
          <div className="hidden md:flex lg:hidden items-center gap-2">
            <a
              href="https://portal.thorpedellow.com"
              target="_blank"
              rel="nofollow noreferrer"
              className={`px-3.5 py-2 rounded-lg text-[11px] font-semibold tracking-wider uppercase transition-all cursor-pointer whitespace-nowrap border ${
                isTransparent ? "border-[#C9A84C]/50 text-[#C9A84C] hover:bg-[#C9A84C]/10" : "border-[#C9A84C]/40 text-[#C9A84C]"
              }`}
            >
              Portal
            </a>
            <Link
              to="/contact"
              className="px-4 py-2 rounded-lg text-[11px] font-bold tracking-wider uppercase cursor-pointer whitespace-nowrap"
              style={{ background: "#071A2E", color: "#fff" }}
            >
              Contact
            </Link>
          </div>

          {/* Hamburger */}
          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
              isTransparent ? "text-white hover:bg-white/10" : "text-[#071A2E] hover:bg-slate-50"
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl leading-none transition-all ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 z-50 w-[85vw] max-w-sm bg-white flex flex-col transition-transform duration-300 ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#071A2E] border border-[#C9A84C]/40">
              <i className="ri-home-4-fill text-[#C9A84C] text-sm leading-none" />
            </div>
            <div>
              <span className="font-bold text-sm text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Thorpe Dellow
              </span>
              <span className="text-[9px] text-[#C9A84C] block tracking-widest uppercase">Structured Investments</span>
            </div>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 cursor-pointer"
          >
            <i className="ri-close-line text-xl leading-none" />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="flex flex-col gap-1 mb-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  location.pathname === link.path
                    ? "text-[#071A2E] bg-[#EEF2F9]"
                    : "text-slate-500 hover:text-[#071A2E] hover:bg-slate-50"
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
                )}
              </Link>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4 mb-4">
            <Link
              to="/brochure"
              className="flex items-center gap-2.5 px-4 py-3.5 rounded-xl text-sm font-semibold text-[#C9A84C] hover:bg-[#C9A84C]/8 transition-all cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-download-line leading-none" />
              </div>
              Investment Brochure
            </Link>
          </div>

          <div className="border-t border-slate-100 pt-4 flex flex-col gap-2.5">
            <a
              href="tel:+442030976185"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-slate-600 text-sm font-medium cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center text-[#C9A84C]">
                <i className="ri-phone-line text-base leading-none" />
              </div>
              +44 20 3097 6185
            </a>
            <a
              href="tel:+14372922296"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 text-slate-600 text-sm font-medium cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center text-[#C9A84C]">
                <i className="ri-phone-line text-base leading-none" />
              </div>
              +1 437 292 2296
            </a>
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="px-4 pb-4 pt-3 border-t border-slate-100 flex flex-col gap-2.5 flex-shrink-0">
          <a
            href="https://portal.thorpedellow.com"
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center justify-center gap-2 py-3 rounded-xl border border-[#C9A84C]/40 text-[#C9A84C] text-sm font-bold uppercase tracking-wide cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line leading-none" />
            </div>
            Client Portal
          </a>
          <Link
            to="/contact"
            className="flex items-center justify-center gap-2 py-3.5 rounded-xl text-white text-sm font-bold uppercase tracking-wide cursor-pointer whitespace-nowrap"
            style={{ background: "#071A2E" }}
          >
            Get in Touch
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line leading-none" />
            </div>
          </Link>
          <p className="text-[10px] text-slate-400 text-center leading-relaxed">
            Authorised &amp; Regulated · Section 21 FSMA 2000 · Capital at Risk
          </p>
        </div>
      </div>
    </>
  );
}
